/* eslint-disable react/button-has-type */
import { Button, Typography } from '@mui/material';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { ExpandDownIcon } from 'src/icons';
import { twMerge } from 'tailwind-merge';
import { Select } from '../elements';
import Loader from '../Loader';

export type TablePaginationState = PaginationState & { totalRecords: number };

/**
 * Defines a type alias 'RowData<T>' which represents the type for each row of data in a table.
 */
export type RowData<T> = T;

export type RowHover = { [key: number]: boolean };

/**
 * Defines an interface 'TableProps<T>' that represents
 * the properties expected by a table component.
 * */
interface TableProps<T> {
  /** An array of 'RowData<T>' or 'undefined' representing the rows of data in the table. */
  data: RowData<T>[] | undefined;
  /** An array of 'ColumnDef<T, any>' that defines the columns of the table. */
  columns: ColumnDef<T, any>[];
  /** A string representing the title of the table. */
  title?: string;
  /** An optional number specifying the number of rows to be displayed per page in the table. */
  pageSize?: number;
  /** An optional callback function triggered when a row in the table is clicked. */
  onRowClick?: (row: T) => void;

  loading?: boolean;

  /** An optional component it renders on the footer end */
  FooterRightComponent?: JSX.Element;

  /**
  * Key to identify the row selection, using this key.
  * By default, it uses the row index.
  */
  rowIdKey?: keyof T;

  /**
  * Optional property to manage the selection state of rows using the provided
  * RowSelectionState object.
  * The keys in the RowSelectionState object correspond to unique identifiers for rows,
  * and the associated boolean values indicate whether the respective row is selected or not.
  *
  * Example: { "rowKey1": true, "rowKey2": false }
  */
  rowSelection?: RowSelectionState;

  /** An optional callback function triggered when row selection change */
  onRowSelection?: (data: RowSelectionState) => void;
  onRowHover?: (data: RowHover) => void;

  /** serverside pagination */
  manualPagination?: TablePaginationState | undefined;
  onChangePagination?: (data: PaginationState) => void;

  /**
   * Remove filters is make to implement the button to remove all the filters
   * in the leaves history table component.
   */
  onClickRemove?: () => void;
  removeFilters?: boolean;

  classes?: {
    tr?: string,
    td?: string,
    th?: string,
  }
}

function Table<T>(props: TableProps<T>) {
  const {
    loading = false,
    columns,
    data = [],
    classes,
    title,
    pageSize = 5,
    rowSelection: inputRowSelection,
    FooterRightComponent,
    rowIdKey,
    removeFilters,
    onClickRemove,
    onRowClick,
    onRowSelection,
    onRowHover,
    manualPagination,
    onChangePagination,
  } = props;

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [currentPageSize, setPageSize] = useState(
    manualPagination?.pageSize || pageSize,
  );
  const [pagination, setPagination] = React.useState({
    pageSize: currentPageSize,
    pageIndex: 0,
  });

  const [updateManualPagination, setManualPagination] = React.useState(
    manualPagination || {
      pageSize,
      pageIndex: 0,
    },
  );

  React.useEffect(() => {
    setManualPagination({ ...updateManualPagination, ...manualPagination });
  }, [JSON.stringify(manualPagination)]);

  React.useEffect(() => {
    if (onChangePagination && manualPagination && (manualPagination.pageSize !== currentPageSize)) {
      setManualPagination({
        ...updateManualPagination,
        pageSize: currentPageSize,
        pageIndex: 0,
      });
    }
    if (!manualPagination) {
      setPagination({ ...pagination, pageSize: currentPageSize, pageIndex: 0 });
    }
  }, [currentPageSize, onChangePagination]);

  React.useEffect(() => {
    if (
      onChangePagination
      && manualPagination
      && (JSON.stringify(updateManualPagination) !== JSON.stringify(manualPagination))
    ) {
      onChangePagination({
        ...updateManualPagination,
      });
    }
    if (!manualPagination) {
      setPagination({ ...pagination, pageSize: currentPageSize });
    }
  }, [updateManualPagination, onChangePagination]);

  React.useEffect(() => {
    setRowSelection(inputRowSelection || {});
  }, [JSON.stringify(inputRowSelection)]);

  React.useEffect(() => {
    if (onRowSelection) {
      onRowSelection(rowSelection);
    }
  }, [JSON.stringify(rowSelection)]);

  const getRowId = (originalRow: RowData<T>, index: number) => `${(rowIdKey ? originalRow[rowIdKey] : index)}`;
  const tableData = React.useMemo(() => data, [JSON.stringify(data)]);
  const table = useReactTable({
    state: {
      rowSelection,
      pagination: manualPagination || pagination,
    },
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: manualPagination ? setManualPagination : setPagination,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    getRowId,
    debugTable: true,
    manualPagination: !!manualPagination,
    pageCount: manualPagination
      ? Math.ceil((manualPagination.totalRecords || 1) / currentPageSize)
      : undefined,
  });

  const [hoveredIndexes, setHoveredIndex] = useState<RowHover>({});

  useEffect(() => {
    if (onRowHover) {
      onRowHover(hoveredIndexes);
    }
  }, [hoveredIndexes]);

  const handleMouseHover = (idx: number) => {
    setHoveredIndex({
      [idx]: true,
    });
  };

  const handleMouseLeave = (idx: number) => {
    const hoveredIndexesCopy = { ...hoveredIndexes };
    delete hoveredIndexesCopy[idx];
    setHoveredIndex(hoveredIndexesCopy);
  };

  return (
    <div className="mx-auto w-full">
      {title && (
        <div className="flex flex-row justify-between h-[3rem] items-center">
          <Typography className="pl-[2.25rem] text-[#28364C] text-[1rem] font-bold">
            {title}
          </Typography>
          {removeFilters ? (
            <Button className="mr-2 mt-4" variant="outlined" size="small" onClick={onClickRemove}>
              Remove filters
            </Button>
          ) : null}
        </div>
      )}
      <table className="mx-auto w-full border-collapse">
        <thead className="bg-[#FBFBFB]">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="px-6 py-0">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={twMerge(
                    'py-0 text-left text-[rgba(40, 54, 76, 1)]',
                    classes?.th,
                  )}
                  style={{ width: `${header.getSize()}px` }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onRowClick) onRowClick(row.original);
              }}
              key={row.id}
              onMouseEnter={() => handleMouseHover(row.index)}
              onMouseLeave={() => handleMouseLeave(row.index)}
              className={twMerge(
                'border-b',
                classes?.tr,
                row.getIsSelected() ? 'custom-shadow bg-primary' : '',
                onRowClick && row.index in hoveredIndexes
                  ? 'cursor-pointer custom-shadow'
                  : '',
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={twMerge(
                    'py-4 text-left font-medium text-gray-900 whitespace-nowrap',
                    classes?.td,
                    row.getIsSelected() ? 'bg-[#ffcccc]' : '',
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {loading && (
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader />
        </div>
      )}
      {!loading && data.length === 0 && (
        <div className="min-h-[50vh] flex items-center justify-center">
          No Data found
        </div>
      )}
      {!loading && data.length !== 0 && (
        <div className="flex justify-center relative items-center p-2 bg-lightGray">
          <div className="absolute left-8">
            {Object.keys(rowSelection).length > 0 && (
              <>
                {Object.keys(rowSelection).length}
                &nbsp; Selected
              </>
            )}
          </div>

          <div className="flex gap-2 items-center">
            <div className="rounded-full flex p-2 borde custom-shadow">
              <ExpandDownIcon
                height={24}
                width={24}
                className={twMerge('rotate-90 cursor-pointer')}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />
              <div className="flex mx-10">
                {table.getState().pagination.pageIndex + 1}
                /
                {table.getPageCount()}
              </div>
              <ExpandDownIcon
                height={24}
                width={24}
                className={twMerge('-rotate-90  cursor-pointer')}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
            </div>
            <Select
              classNames={{
                root: 'min-w-[80px] max-w-[80px]',
              }}
              value={currentPageSize}
              options={[
                // { label: '4', value: 4 },
                { label: '5', value: 5 },
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
                { label: '500', value: 500 },
              ]}
              onChange={(e) => setPageSize(e)}
            />
          </div>

          <div className="absolute right-8">{FooterRightComponent}</div>
          <div />
        </div>
      )}
    </div>
  );
}

Table.defaultProps = {
  pageSize: 5,
  loading: false,
  rowIdKey: undefined,
  title: undefined,
  rowSelection: {},
  FooterRightComponent: null,
  removeFilters: false,
  onClickRemove: () => { },
  onRowClick: () => { },
  onRowSelection: () => { },
  onRowHover: () => { },

  // manual pagination
  manualPagination: undefined,
  onChangePagination: undefined,
  classes: {
    tr: '',
    td: '',
    th: '',
  },
};

Table.propTypes = {};

export default Table;
